"use client";

import { useEffect, useMemo, useState } from "react";
import type { ComponentProps } from "react";
import { useTheme } from "next-themes";

import BlurImage from "@/components/shared/blur-image";

type BlurImageProps = ComponentProps<typeof BlurImage>;

type AuthorAvatarProps = Pick<BlurImageProps, "alt" | "className" | "priority" | "width" | "height"> & {
  lightSrc?: string | null;
  darkSrc?: string | null;
  fallbackSrc: string;
  lightBlurDataURL?: string;
  darkBlurDataURL?: string;
  fallbackBlurDataURL: string;
};

export default function AuthorAvatar({
  lightSrc,
  darkSrc,
  fallbackSrc,
  lightBlurDataURL,
  darkBlurDataURL,
  fallbackBlurDataURL,
  ...imageProps
}: AuthorAvatarProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { src, blurDataURL } = useMemo(() => {
    if (!mounted) {
      return {
        src: fallbackSrc,
        blurDataURL: fallbackBlurDataURL,
      };
    }

    if (resolvedTheme === "dark" && darkSrc) {
      return {
        src: darkSrc,
        blurDataURL: darkBlurDataURL ?? fallbackBlurDataURL,
      };
    }

    if (resolvedTheme === "light" && lightSrc) {
      return {
        src: lightSrc,
        blurDataURL: lightBlurDataURL ?? fallbackBlurDataURL,
      };
    }

    return {
      src: fallbackSrc,
      blurDataURL: fallbackBlurDataURL,
    };
  }, [darkBlurDataURL, darkSrc, fallbackBlurDataURL, fallbackSrc, lightBlurDataURL, lightSrc, mounted, resolvedTheme]);

  return (
    <BlurImage
      {...imageProps}
      src={src}
      placeholder="blur"
      blurDataURL={blurDataURL}
    />
  );
}
